import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import queryString from "query-string";
import { bindActionCreators } from "redux";
import moment from "moment";

import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";
import TimeTrackerFilters from "./TimeTrackerFilters";
import ReportService from "../../../services/reportService";

import { formatDate } from "../../../helpers/date";
import { downloadTimeTrackerCSV } from "../utils";

import TimeTrackerList from "./TimeTrackerList";

import {
  PROJECT_COLUMNS,
  CALL_COLUMNS,
  CALL_COLUMNS_WITH_NOTE,
  PREPAID_COLUMNS
} from "./columns";

import "./TimeTrackerPage.scss";

const TimeTrackerPage = ({ codes, actions, history, exported, exporting }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: moment().startOf("year").format("YYYY-MM-DD"),
    endDate: formatDate(moment(), "yyyy-MM-dd"),
    activeOnly: true
  });
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Time-Tracker-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setData([]);
    const queryFilters = {
      ...filters,
      contract_type: filters.contract_type?.map((c) => c.value),
      stage: filters.stage?.map((c) => c.value),
      customer: filters.customer?.value
    };
    const query = queryString.stringify(queryFilters, {
      arrayFormat: "bracket"
    });
    const result = await ReportService.get(`/time-tracker?${query}`);
    setData(result);
    setLoading(false);
  }, [filters]);

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (Array.isArray(value) && name === "customers") {
      setFilters((prev) => ({ ...prev, [name]: value.map((v) => v.value) }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  // useEffect(() => {
  //   fetchData();
  // }, [fetchData]);

  const handleRowClick = (rowInfo) => {
    history.push(`/app/contracts/${rowInfo.original._id}`);
  };

  const handleExport = () => {
    const queryFilters = {
      ...filters,
      contract_type: filters.contract_type?.map((c) => c.value),
      stage: filters.stage?.map((c) => c.value),
      customer: filters.customer?.value
    };
    const payload = {
      data: queryFilters,
      type: "TIME_TRACKER",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  const handleExportCSV = () => {
    downloadTimeTrackerCSV(
      `TimeTracker-${formatDate(moment(), "yyyyMMdd")}`,
      data,
      PREPAID_COLUMNS(),
      filters.showNotes ? CALL_COLUMNS_WITH_NOTE() : CALL_COLUMNS(),
      PROJECT_COLUMNS(),
      filters.startDate,
      filters.endDate
    );
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().startOf("year").format("YYYY-MM-DD"),
      endDate: formatDate(moment(), "yyyy-MM-dd"),
      activeOnly: true
    });
    setData([]);
  };

  const isExportDisabled =
    !filters.customer || !filters.startDate || !filters.endDate;

  return (
    <div id="time-tracker-page" className="report-page">
      <TimeTrackerFilters
        filters={filters}
        codes={codes}
        onChange={handleChange}
        fetchData={fetchData}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || exporting || isExportDisabled}
        loading={loading || exporting}
        onCSVExport={handleExportCSV}
        onPDFExport={handleExport}
      />
      <TimeTrackerList
        codes={codes}
        data={data}
        loading={loading}
        onRowClick={handleRowClick}
        filters={filters}
      />
    </div>
  );
};

TimeTrackerPage.propTypes = {
  history: PropTypes.object,
  codes: PropTypes.array,
  actions: PropTypes.object,
  exported: PropTypes.string,
  exporting: PropTypes.bool
};
const mapStatesToProps = (state) => ({
  codes: state.reducers.codes,
  exported: state.reducers.exported,
  exporting: state.reducers.exporting
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions,
      ...exportActions
    },
    dispatch
  )
});
export default connect(mapStatesToProps, mapDispatchToProps)(TimeTrackerPage);
